import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, Loader2, PlayCircle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { formatDate } from '@/lib/utils/date';
import { useUserDetails } from '@/features/users/api';
import { useSubUserReport } from '../api';
import type {
  CampaignTypeFilter,
  PatchDurationOp,
  SubUserReportRow,
} from '../types';
import { dashIfEmpty, maskPhone, templateLabel } from '../utils';

const CAMPAIGN_TYPE_OPTIONS: Array<{ value: CampaignTypeFilter; label: string }> = [
  { value: '', label: 'All campaign types' },
  { value: '1', label: 'DTMF' },
  { value: '2', label: 'Call Patch' },
];

const PATCH_OP_OPTIONS: Array<{ value: PatchDurationOp; label: string }> = [
  { value: '', label: 'Any duration' },
  { value: '>', label: 'Greater than' },
  { value: '<', label: 'Less than' },
  { value: '=', label: 'Equal to' },
];

/** Sentinel for "all" / "any" Select values (Radix forbids empty strings). */
const ANY = '__any__';

export function SubUserReportPage() {
  const userId = useAuthStore((s) => s.userId);
  const profile = useUserDetails(userId);

  /* Module 8 enables BNI masking in the UI. */
  const maskNumbers = useMemo(
    () => (profile.data?.modules ?? []).some((m) => m.moduleId === 8),
    [profile.data?.modules],
  );

  /* Filter state. */
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [campaignType, setCampaignType] = useState<CampaignTypeFilter>('');
  const [campaignName, setCampaignName] = useState('');
  const [mobile, setMobile] = useState('');
  const [agentNumber, setAgentNumber] = useState('');
  const [patchOp, setPatchOp] = useState<PatchDurationOp>('');
  const [patchDuration, setPatchDuration] = useState('');

  /* On first render, hold the full unfiltered result so the campaign-name
   * dropdown can populate from every campaign the user has ever run. */
  const [allRows, setAllRows] = useState<SubUserReportRow[]>([]);

  const reportMutation = useSubUserReport();

  const runQuery = (overrides?: Partial<{ campaignName: string }>) => {
    if (!userId) return;
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date');
      return;
    }
    reportMutation.mutate(
      {
        userId: String(userId),
        startDate: startDate ? formatDate(startDate) : '',
        endDate: endDate ? formatDate(endDate) : '',
        campaignName: overrides?.campaignName ?? campaignName,
        campaignType,
        mobile: mobile.trim(),
        patchDurationOperation: patchOp,
        pd: patchOp ? patchDuration.trim() : '',
        agentNumber: agentNumber.trim(),
      },
      {
        onSuccess: (data) => {
          /* First (no-filter) call seeds the campaign-name dropdown. */
          if (
            allRows.length === 0 &&
            !startDate &&
            !endDate &&
            !campaignType &&
            !campaignName &&
            !mobile &&
            !agentNumber &&
            !patchOp
          ) {
            setAllRows(Array.isArray(data) ? data : []);
          }
        },
      },
    );
  };

  /* Initial unfiltered load so the campaign-name dropdown has options. */
  useEffect(() => {
    if (!userId) return;
    runQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const campaignNames = useMemo(() => {
    const set = new Set<string>();
    allRows.forEach((r) => {
      if (r.campaignName) set.add(r.campaignName);
    });
    return Array.from(set).sort();
  }, [allRows]);

  const [playUrl, setPlayUrl] = useState<string | null>(null);

  const columns = useMemo<ColumnDef<SubUserReportRow>[]>(
    () => [
      {
        accessorKey: 'campaignId',
        header: 'Campaign ID',
        size: 120,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {dashIfEmpty(row.original.campaignId)}
          </span>
        ),
      },
      {
        accessorKey: 'campaignName',
        header: 'Campaign',
        cell: ({ row }) => dashIfEmpty(row.original.campaignName),
      },
      {
        accessorKey: 'bni',
        header: 'Mobile',
        cell: ({ row }) =>
          maskNumbers ? maskPhone(row.original.bni) : dashIfEmpty(row.original.bni),
      },
      { accessorKey: 'cli', header: 'CLI', cell: ({ row }) => dashIfEmpty(row.original.cli) },
      {
        accessorKey: 'startTime',
        header: 'Start',
        size: 170,
        cell: ({ row }) => dashIfEmpty(row.original.startTime),
      },
      {
        accessorKey: 'answerTime',
        header: 'Answered',
        size: 170,
        cell: ({ row }) => dashIfEmpty(row.original.answerTime),
      },
      {
        accessorKey: 'endTime',
        header: 'Ended',
        size: 170,
        cell: ({ row }) => dashIfEmpty(row.original.endTime),
      },
      {
        accessorKey: 'billingDuration',
        header: 'Billing duration',
        size: 130,
        cell: ({ row }) => (
          <span className="tabular-nums">
            {dashIfEmpty(row.original.billingDuration)}
          </span>
        ),
      },
      {
        accessorKey: 'answerStatus',
        header: 'Status',
        cell: ({ row }) => dashIfEmpty(row.original.answerStatus),
      },
      {
        accessorKey: 'hangupCause',
        header: 'Hangup cause',
        cell: ({ row }) => dashIfEmpty(row.original.hangupCause),
      },
      {
        accessorKey: 'agentNumber',
        header: 'Agent',
        cell: ({ row }) =>
          maskNumbers
            ? maskPhone(row.original.agentNumber)
            : dashIfEmpty(row.original.agentNumber),
      },
      {
        accessorKey: 'bridgeTime',
        header: 'Bridge time',
        size: 170,
        cell: ({ row }) => dashIfEmpty(row.original.bridgeTime),
      },
      {
        accessorKey: 'bridgeRingDuration',
        header: 'Bridge ring',
        size: 130,
        cell: ({ row }) => dashIfEmpty(row.original.bridgeRingDuration),
      },
      {
        accessorKey: 'patchDuration',
        header: 'Patch duration',
        size: 130,
        cell: ({ row }) => dashIfEmpty(row.original.patchDuration),
      },
      {
        accessorKey: 'bridgeHangupCause',
        header: 'Bridge hangup',
        cell: ({ row }) => dashIfEmpty(row.original.bridgeHangupCause),
      },
      { accessorKey: 'dtmf', header: 'DTMF', cell: ({ row }) => dashIfEmpty(row.original.dtmf) },
      {
        accessorKey: 'template',
        header: 'Template',
        size: 120,
        cell: ({ row }) => <Badge variant="outline">{templateLabel(row.original.template)}</Badge>,
      },
      {
        id: 'recording',
        header: 'Recording',
        size: 140,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const url = row.original.recordingFile;
          if (!url) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Listen"
                onClick={() => setPlayUrl(url)}
              >
                <PlayCircle className="h-4 w-4" />
              </Button>
              <Button asChild variant="ghost" size="icon" className="h-8 w-8" title="Download">
                <a href={url} download target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4" />
                </a>
              </Button>
            </div>
          );
        },
      },
    ],
    [maskNumbers],
  );

  const rows = (reportMutation.data ?? []) as SubUserReportRow[];

  return (
    <FillPage>
      <PageHeader
        title="My Activity"
        description="Filter your call records by date, campaign, mobile, agent, or patch duration."
      />

      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-4">
          <div className="space-y-1">
            <Label>From</Label>
            <DatePicker value={startDate ?? null} onChange={setStartDate} placeholder="Any date" />
          </div>
          <div className="space-y-1">
            <Label>To</Label>
            <DatePicker value={endDate ?? null} onChange={setEndDate} placeholder="Any date" />
          </div>
          <div className="space-y-1">
            <Label>Campaign type</Label>
            <Select
              value={campaignType === '' ? ANY : campaignType}
              onValueChange={(v) => setCampaignType(v === ANY ? '' : (v as CampaignTypeFilter))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CAMPAIGN_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || ANY} value={opt.value === '' ? ANY : opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Campaign name</Label>
            <Select
              value={campaignName === '' ? ANY : campaignName}
              onValueChange={(v) => setCampaignName(v === ANY ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All campaigns" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All campaigns</SelectItem>
                {campaignNames.map((n) => (
                  <SelectItem key={n} value={n}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="sur-mobile">Mobile</Label>
            <Input
              id="sur-mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, ''))}
              inputMode="tel"
              placeholder="Any"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sur-agent">Agent number</Label>
            <Input
              id="sur-agent"
              value={agentNumber}
              onChange={(e) => setAgentNumber(e.target.value.replace(/[^\d+]/g, ''))}
              inputMode="tel"
              placeholder="Any"
            />
          </div>
          <div className="space-y-1">
            <Label>Patch duration</Label>
            <Select
              value={patchOp === '' ? ANY : patchOp}
              onValueChange={(v) => {
                const next = v === ANY ? '' : (v as PatchDurationOp);
                setPatchOp(next);
                if (!next) setPatchDuration('');
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PATCH_OP_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || ANY} value={opt.value === '' ? ANY : opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {patchOp && (
            <div className="space-y-1">
              <Label htmlFor="sur-pd">Duration (seconds)</Label>
              <Input
                id="sur-pd"
                value={patchDuration}
                onChange={(e) => setPatchDuration(e.target.value.replace(/[^\d.]/g, ''))}
                inputMode="numeric"
                placeholder="e.g. 30"
              />
            </div>
          )}

          <div className="md:col-span-4 md:flex md:justify-end">
            <Button onClick={() => runQuery()} disabled={reportMutation.isPending}>
              {reportMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<SubUserReportRow, unknown>
        tableId="reports:sub-user:v1"
        columns={columns}
        data={rows}
        isLoading={reportMutation.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle="No records"
        emptyDescription="No call records match the current filters."
        getRowId={(row, idx) => row.id ?? `sur-${idx}`}
        exportFilename="sub-user-report.csv"
        initialColumnPinning={{ right: ['recording'] }}
        pageSize={10}
      />

      <Dialog open={playUrl !== null} onOpenChange={(o) => !o && setPlayUrl(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Recording</DialogTitle>
            <DialogDescription>Listen to the call recording.</DialogDescription>
          </DialogHeader>
          {playUrl && (
            <audio src={playUrl} controls autoPlay className="w-full">
              Your browser does not support audio playback.
            </audio>
          )}
        </DialogContent>
      </Dialog>
    </FillPage>
  );
}
